import {
  Shield,
  Plus,
  Edit,
  Eye,
  User as UserIcon,
  Mail,
  Lock,
  Phone,
  Building2,
  ChevronRight,
  Check,
  Settings2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  X,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { FormError } from '../components/ui/FormError'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import { Spinner, simulateBackendDelay } from '@/app/lib/loader/loader';
import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

// Hooks
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z, { number } from 'zod'
import { http } from "@/app/lib/http/client"
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { userService } from '../services/user.service'
import { sucursalService } from '../services/sucursal.service'
import { useSessionStore } from '../store/auth/auth.store'
import { authService } from '../services/auth.service'

// ─── Types ───────────────────────────────────────────────────────────────────

interface User {
  id: number
  name: string
  last_name: string
  email: string
  phone_number: string
  avatar_url?: string
  role: string
  branch: string
  lastAccess: string
  active: boolean
  use_pin_login: boolean
  role_id: number
  access_all_sucursals: boolean
  sucursals?: { id: number; name: string }[]
  sucursal_ids?: number[]
}

interface NewUserForm {
  nombre: string
  apellidos: string
  email: string
  telefono: string
  password?: string
  confirmPassword?: string
  rol: string
  sucursales: string[]
  activo: boolean
  use_pin_login: boolean
  pin?: string
  confirmarPin?: string
  access_all_sucursals: boolean
}

interface Permission {
  id: string
  name: string
  description: string;
  is_group: boolean;
  parent: string | number | null;
  action: string | null;
  code: number;
}

interface Role {
  id: string
  name: string
  description: string
  color: string // tailwind bg token e.g. "bg-red-100 text-red-700"
  badgeColor: string // hex for dot indicator
  isSystem: boolean // system roles can't be deleted or renamed
  active: boolean
  permissions: string[]
  users_count: number
}

// ─── Static data ─────────────────────────────────────────────────────────────
/*
const allPermissions: Permission[] = [
  { id: 'pos_sell', name: 'Realizar ventas', category: 'Punto de Venta' },
  {
    id: 'pos_discount',
    name: 'Aplicar descuentos',
    category: 'Punto de Venta',
  },
  { id: 'pos_cancel', name: 'Cancelar ventas', category: 'Punto de Venta' },
  { id: 'pos_cashout', name: 'Corte de caja', category: 'Punto de Venta' },
  { id: 'inv_view', name: 'Ver inventario', category: 'Inventario' },
  { id: 'inv_adjust', name: 'Ajustar inventario', category: 'Inventario' },
  { id: 'inv_transfer', name: 'Transferir productos', category: 'Inventario' },
  {
    id: 'inv_alerts',
    name: 'Gestionar alertas de stock',
    category: 'Inventario',
  },
  { id: 'pur_create', name: 'Crear órdenes de compra', category: 'Compras' },
  { id: 'pur_receive', name: 'Recibir mercancía', category: 'Compras' },
  { id: 'pur_approve', name: 'Aprobar órdenes', category: 'Compras' },
  { id: 'rep_view', name: 'Ver reportes', category: 'Reportes' },
  { id: 'rep_export', name: 'Exportar reportes', category: 'Reportes' },
  { id: 'rep_advanced', name: 'Reportes avanzados', category: 'Reportes' },
  { id: 'cli_view', name: 'Ver clientes', category: 'Clientes' },
  { id: 'cli_edit', name: 'Editar clientes', category: 'Clientes' },
  { id: 'cli_loyalty', name: 'Gestionar lealtad', category: 'Clientes' },
  { id: 'reg_view', name: 'Ver módulo regulatorio', category: 'Regulatorio' },
  {
    id: 'reg_controlled',
    name: 'Manejar controlados',
    category: 'Regulatorio',
  },
  {
    id: 'reg_antibiotics',
    name: 'Manejar antibióticos',
    category: 'Regulatorio',
  },
  { id: 'usr_manage', name: 'Gestionar usuarios', category: 'Administración' },
  { id: 'set_config', name: 'Configurar sistema', category: 'Administración' },
  {
    id: 'branch_manage',
    name: 'Gestionar sucursales',
    category: 'Administración',
  },
]
*/

const ROLE_COLORS = [
  { label: 'Rojo', badge: 'bg-red-100 text-red-700', dot: '#ef4444' },
  { label: 'Azul', badge: 'bg-blue-100 text-blue-700', dot: '#3b82f6' },
  { label: 'Verde', badge: 'bg-green-100 text-green-700', dot: '#22c55e' },
  { label: 'Morado', badge: 'bg-purple-100 text-purple-700', dot: '#a855f7' },
  { label: 'Naranja', badge: 'bg-orange-100 text-orange-700', dot: '#f97316' },
  { label: 'Cyan', badge: 'bg-cyan-100 text-cyan-700', dot: '#06b6d4' },
  { label: 'Amarillo', badge: 'bg-yellow-100 text-yellow-700', dot: '#eab308' },
  { label: 'Slate', badge: 'bg-slate-100 text-slate-700', dot: '#64748b' },
]
/*
const initialRoles: Role[] = [
  {
    id: 'admin',
    name: 'Administrador',
    description:
      'Acceso completo al sistema, configuración global y gestión de todos los módulos.',
    color: 'bg-red-100 text-red-700',
    badgeColor: '#ef4444',
    isSystem: true,
    active: true,
    permissions: allPermissions.map((p) => p.id),
  },
  {
    id: 'manager',
    name: 'Encargado',
    description:
      'Gestión operativa de sucursal, reportes, compras e inventario.',
    color: 'bg-blue-100 text-blue-700',
    badgeColor: '#3b82f6',
    isSystem: true,
    active: true,
    permissions: [
      'pos_sell',
      'pos_discount',
      'pos_cancel',
      'pos_cashout',
      'inv_view',
      'inv_adjust',
      'inv_transfer',
      'inv_alerts',
      'pur_create',
      'pur_receive',
      'rep_view',
      'rep_export',
      'cli_view',
      'cli_edit',
      'cli_loyalty',
    ],
  },
  {
    id: 'seller',
    name: 'Vendedor',
    description:
      'Operación del punto de venta, consulta de inventario y atención a clientes.',
    color: 'bg-green-100 text-green-700',
    badgeColor: '#22c55e',
    isSystem: true,
    active: true,
    permissions: ['pos_sell', 'inv_view', 'cli_view'],
  },
  {
    id: 'pharmacy',
    name: 'Farmacéutico',
    description:
      'Acceso a módulo regulatorio, recetas controladas y antibióticos.',
    color: 'bg-purple-100 text-purple-700',
    badgeColor: '#a855f7',
    isSystem: true,
    active: true,
    permissions: [
      'pos_sell',
      'inv_view',
      'cli_view',
      'reg_view',
      'reg_controlled',
      'reg_antibiotics',
    ],
  },
]*/

const initialUsers: User[] = [
  /*

  / ───── Comentado para no mostrar datos estaticos de usuarios ─────────────────────────────────────────────────────────────
  {
    id: 1,
    name: 'Admin Principal',
    email: 'admin@farmacontrol.com',
    role: 'admin',
    branch: 'Todas',
    lastAccess: '2026-05-27 09:45',
    active: true,
  },
  {
    id: 2,
    name: 'María López',
    email: 'maria.lopez@farmacontrol.com',
    role: 'manager',
    branch: 'Sucursal Centro',
    lastAccess: '2026-05-27 14:20',
    active: true,
  },
  {
    id: 3,
    name: 'Carlos Méndez',
    email: 'carlos.mendez@farmacontrol.com',
    role: 'seller',
    branch: 'Sucursal Norte',
    lastAccess: '2026-05-27 11:15',
    active: true,
  },*/
]

const emptyForm: NewUserForm = {
  nombre: '',
  apellidos: '',
  email: '',
  telefono: '',
  password: '',
  confirmPassword: '',
  rol: 'seller',
  sucursales: [],
  activo: true,
  use_pin_login: false,
  pin: '',
  confirmarPin: '',
  access_all_sucursals: false,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function groupByCategory(perms: Permission[]) {
  const groupedData: Record<string, Permission[]> = {}
  const modulosPadre = perms.filter(p => p.parent === null)
  modulosPadre.forEach(padre => {
    const hijos = perms.filter(p => p.parent === padre.id)
    groupedData[padre.name] = hijos
  })

  return groupedData
}

// ─── Esquemas de validación para usuarios ─────────────────────────────────────────────────
const userSchema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  apellidos: z.string().min(1, 'Los apellidos son requeridos'),
  email: z.string().email('Debes ingresar un correo válido'),
  telefono: z.string().optional().refine(val => !val || val.trim() === '' || val.length === 10, {
    message: 'El teléfono debe tener exactamente 10 caracteres',
  }),
  password: z.string().optional(),
  confirmarPassword: z.string().optional(),
  rol: z.string().min(1, 'El rol es requerido'),
  sucursales: z.array(z.string()).optional(),
  activo: z.boolean(),
  use_pin_login: z.boolean(),
  pin: z.string().optional(),
  confirmarPin: z.string().optional(),
  access_all_sucursals: z.boolean(),
}).superRefine((data, ctx) => {
  if (data.password && data.password.length > 0 && data.password.length < 6) {
    ctx.addIssue({
      code: 'custom',
      message: 'La contraseña debe tener al menos 6 caracteres',
      path: ['password'],
    });
  }
  if (data.password && data.password !== data.confirmarPassword) {
    ctx.addIssue({
      code: 'custom',
      message: 'Las contraseñas no coinciden',
      path: ['confirmarPassword'],
    });
  }
  if (data.use_pin_login) {
    if (data.pin && (data.pin.length !== 4 || !/^\d{4}$/.test(data.pin))) {
      ctx.addIssue({
        code: 'custom',
        message: 'El PIN debe ser de 4 dígitos numéricos',
        path: ['pin'],
      });
    }
    if (data.pin && data.pin !== data.confirmarPin) {
      ctx.addIssue({
        code: 'custom',
        message: 'Los PINs no coinciden',
        path: ['confirmarPin'],
      });
    }
  }
  if (!data.access_all_sucursals && (!data.sucursales || data.sucursales.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Selecciona al menos una sucursal o "Todas las sucursales"',
      path: ['sucursales'],
    });
  }
});

type UserFormValidate = z.infer<typeof userSchema>;

// ─── Esquemas de validación para roles ─────────────────────────────────────────────────
const roleSchema = z.object({
  nombre: z.string().min(1, 'El nombre del rol es requerido'),
  descripcion: z.string().min(1, 'La descripcion es requerida'),
  permisos: z.array(z.string()).min(1, 'Selecciona al menos un permiso'),
  color: z.string().min(1, 'El color es requerido'),
  activo: z.boolean(),
});

type RoleFormValidate = z.infer<typeof roleSchema>;

// ─── Esquema de validación para Permisos ────────────────────────────────────
const permissionSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  is_group: z.boolean(),
  parent: z.string().optional().nullable(), // Puede ser texto o null
  action: z.string().optional(),
}).superRefine((data, ctx) => {
  if (!data.is_group) {
    if (!data.parent) {
      ctx.addIssue({
        code: 'custom',
        message: 'Debes seleccionar un grupo padre',
        path: ['parent'], // Donde mostrar el error
      });
    }
    if (!data.action || data.action.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'El codigo de acción es requerido',
        path: ['action'],
      });
    }
  }
});

type PermissionFormValidate = z.infer<typeof permissionSchema>;

// ─── Componente ────────────────────────────────────────────────────────────────

export function Users() {
  const sessionStore = useSessionStore()
  //Formulario para los usuarios
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    getValues,
    setError,
    formState: { errors, isSubmitting }
  } = useForm<UserFormValidate>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      nombre: '',
      apellidos: '',
      email: '',
      telefono: '',
      password: '',
      confirmarPassword: '',
      rol: 'seller',
      sucursales: [],
      activo: true,
      use_pin_login: false,
      pin: '',
      confirmarPin: '',
      access_all_sucursals: false,
    },
  });
  //Formulario para los roles
  const {
    register: registerRole,
    handleSubmit: handleSubmitRole,
    setValue: setValueRole,
    watch: watchRole,
    reset: resetRole,
    formState: { errors: errorsRole, isSubmitting: isSubmittingRole }
  } = useForm<RoleFormValidate>({
    resolver: zodResolver(roleSchema),
    mode: 'onChange',
    defaultValues: {
      nombre: '',
      descripcion: '',
      permisos: [],
      color: '',
      activo: true,
    },
  });

  //Formulario para los permisos
  const {
    register: registerPerm,
    handleSubmit: handleSubmitPerm,
    watch: watchPerm,
    setValue: setValuePerm,
    reset: resetPerm,
    formState: { errors: errorsPerm, isSubmitting: isSubmittingPerm }
  } = useForm<PermissionFormValidate>({
    resolver: zodResolver(permissionSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      is_group: true,
      parent: null,
      action: '',
    },
  });

  //Variables de permisos
  const isGroupSelected = watchPerm('is_group');

  //Variables de usuarios
  const activoValue = watch('activo');
  const sucursalesValue = watch('sucursales');
  const rolValue = watch('rol');
  //Variables de roles
  const colorSeleccionado = watchRole('color');
  const permisosSeleccionados = watchRole('permisos');





  // Existing user permission modal
  const [refreshCount, setRefreshCount] = useState(0);
  const [editingPermId, setEditingPermId] = useState<string | null>(null);

  const [showNewUserModal, setShowNewUserModal] = useState(false)
  const [editingUserId, setEditingUserId] = useState<number | null>(null)
  const [deleteUserConfirmId, setDeleteUserConfirmId] = useState<number | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('')
  const [pagination, setPagination] = useState({
      currentPage: 1,
      total: 0,
      perPage: 10,
      lastPage: 1,
      from: 0,
      to: 0,
  })
  const [sucursales, setSucursales] = useState<any[]>([])
  const [previousSucursales, setPreviousSucursales] = useState<string[]>([]);
  // const [newUserForm, setNewUserForm] = useState<NewUserForm>(emptyForm)
  const [formStep, setFormStep] = useState<1 | 2>(1)
  //const [formErrors, setFormErrors] = useState<  Partial<Record<keyof NewUserForm, string>>>({})

  // Roles manager modal
  // Estado para saber si estamos creando un permiso
  const [isCreatingPermission, setIsCreatingPermission] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false)
  const [roles, setRoles] = useState<Role[]>([])
  const [dbPermissions, setDbPermissions] = useState<Permission[]>([])
  const [activeRoleId, setActiveRoleId] = useState<string>('admin')
  const [isSwitchingRole, setIsSwitchingRole] = useState(false)
  const [isCreatingRole, setIsCreatingRole] = useState(false)
  const [editDraft, setEditDraft] = useState<Role | null>(null)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(),
  )
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deletePermConfirmId, setDeletePermConfirmId] = useState<string | null>(null);
  const [statusConfirmId, setStatusConfirmId] = useState<string | null>(null);

  const grouped = groupByCategory(dbPermissions)

  // ── New user modal helpers ──────────────────────────────────────────────────

  const toggleSucursal = (id: string) => {
    const current = sucursalesValue || [];

    if (id === 'todas') {
      if (current.includes('todas')) {
        // Deseleccionamos todas, restauramos las anteriores
        setValue('sucursales', previousSucursales, { shouldValidate: true });
        setValue('access_all_sucursals', false, { shouldValidate: true });
      } else {
        // Seleccionamos todas, guardamos las actuales
        setPreviousSucursales(current.filter(s => s !== 'todas'));
        setValue('sucursales', ['todas'], { shouldValidate: true });
        setValue('access_all_sucursals', true, { shouldValidate: true });
      }
      return;
    }

    const withoutAll = current.filter((s) => s !== 'todas');
    let newSelection = withoutAll;

    if (withoutAll.includes(id)) {
      newSelection = withoutAll.filter((s) => s !== id);
    } else {
      newSelection = [...withoutAll, id];
    }

    // Verificar si se seleccionaron todas las sucursales individuales
    const allIndividualIds = sucursales.filter(s => s.id !== 'todas').map(s => s.id);
    const areAllSelected = allIndividualIds.length > 0 && allIndividualIds.every(sId => newSelection.includes(sId));

    if (areAllSelected) {
      setPreviousSucursales(allIndividualIds);
      setValue('sucursales', ['todas'], { shouldValidate: true });
      setValue('access_all_sucursals', true, { shouldValidate: true });
    } else {
      setValue('sucursales', newSelection, { shouldValidate: true });
      setValue('access_all_sucursals', false, { shouldValidate: true });
    }
  }

  /*const validateStep1 = () => {
    const e: Partial<Record<keyof NewUserForm, string>> = {}
    if (!newUserForm.nombre.trim()) e.nombre = 'El nombre es requerido'
    if (!newUserForm.apellidos.trim())
      e.apellidos = 'Los apellidos son requeridos'
    if (!newUserForm.email.trim() || !/\S+@\S+\.\S+/.test(newUserForm.email))
      e.email = 'Email inválido'
    if (!newUserForm.password || newUserForm.password.length < 8)
      e.password = 'Mínimo 8 caracteres'
    if (newUserForm.password !== newUserForm.confirmPassword)
      e.confirmPassword = 'Las contraseñas no coinciden'
    setFormErrors(e)
    return Object.keys(e).length === 0
  }*/

  /* const validateStep2 = () => {
     const e: Partial<Record<keyof NewUserForm, string>> = {}
     if (newUserForm.sucursales.length === 0)
       e.sucursales = 'Selecciona al menos una sucursal'
     setFormErrors(e)
     return Object.keys(e).length === 0
   }*/


  const openNewUserModal = () => {
    reset({
      nombre: '',
      apellidos: '',
      email: '',
      telefono: '',
      password: '',
      confirmarPassword: '',
      rol: roles[0]?.id || '',
      sucursales: [],
      activo: true,
      use_pin_login: false,
      pin: '',
      confirmarPin: '',
      access_all_sucursals: false,
    });
    setEditingUserId(null);
    setAvatarFile(null);
    setFormStep(1);
    setShowNewUserModal(true);
  }


  const openEditUserModal = (user: User) => {
    setAvatarFile(null);
    reset({
      nombre: user.name,
      apellidos: user.last_name || '',
      email: user.email,
      telefono: user.phone_number || '',
      password: '',
      confirmarPassword: '',
      rol: String(user.role_id),
      sucursales: user.access_all_sucursals ? ['todas'] : (user.sucursals?.map(s => String(s.id)) || []),
      activo: user.active,
      use_pin_login: user.use_pin_login,
      pin: '',
      confirmarPin: '',
      access_all_sucursals: user.access_all_sucursals,
    });
    setEditingUserId(user.id);
    setFormStep(1);
    setShowNewUserModal(true);
  }

  // ── Roles modal helpers ─────────────────────────────────────────────────────

  const openRolesModal = () => {
    setActiveRoleId(roles[0]?.id ?? '')
    setEditDraft(null)
    setIsCreatingRole(false)
    setDeleteConfirmId(null)
    setShowRolesModal(true)
  }

  const startEdit = (role: Role) => {
    setEditDraft({ ...role, permissions: [...role.permissions] });
    setIsCreatingRole(false);
    setValueRole('nombre', role.name);
    setValueRole('descripcion', role.description);
    setValueRole('color', role.color);
    setValueRole('activo', role.active);
    setValueRole('permisos', [...role.permissions]);
  };

  const startCreate = () => {
    const draft: Role = {
      id: `custom_${Date.now()}`,
      name: '',
      description: '',
      color: 'bg-slate-100 text-slate-700',
      badgeColor: '#64748b',
      isSystem: false,
      active: true,
      permissions: [],
      users_count: 0,
    }
    setEditDraft(draft)
    setIsCreatingRole(true)
  }

  const cancelEdit = () => {
    setEditDraft(null)
    setIsCreatingRole(false)
  }

  const saveDraft = () => {
    if (!editDraft || !editDraft.name.trim()) return
    if (isCreatingRole) {
      setRoles((prev) => [...prev, editDraft])
      setActiveRoleId(editDraft.id)
    } else {
      setRoles((prev) =>
        prev.map((r) => (r.id === editDraft.id ? editDraft : r)),
      )
    }
    setEditDraft(null)
    setIsCreatingRole(false)
  }

  const toggleRoleActive = (id: string) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r)),
    )
  }

  // ─── Eliminar rol ───────────────────────────────────────────────────────────
  const deleteRole = async (id: string) => {
    try {
      await http.delete(`/authorization/roles/${id}`);
      toast.success("Rol eliminado con éxito");
      setDeleteConfirmId(null);
      if (activeRoleId === id) setActiveRoleId('');
      if (editDraft?.id === id) setEditDraft(null);

      // 3. ¡La magia! Actualizamos la tabla automáticamente
      setRefreshCount(prev => prev + 1);

    } catch (error) {
      toast.error("Error al eliminar", {
        description: "No se pudo eliminar el rol en el servidor."
      });
      console.error(error);
    }
  };

  // ─── Eliminar Permiso (La llamada real) ──────────────────────────────────────
  const executeDeletePermiso = async () => {
    if (!deletePermConfirmId) return;
    try {
      await http.delete(`/authorization/permissions/${deletePermConfirmId}`);
      toast.success("Permiso eliminado");
      setRefreshCount(prev => prev + 1);
    } catch (error) {
      toast.error("Error", { description: "No se pudo eliminar el permiso." });
    } finally {
      setDeletePermConfirmId(null); // Cerramos el modal bonito
    }
  };

  const executeDeleteRole = () => {
    if (deleteConfirmId) {
      deleteRole(deleteConfirmId);
    }
  };

  const executeChangeStatus = async () => {
    if (statusConfirmId) {
      const roleToToggle = roles.find((r) => r.id === statusConfirmId);
      if (roleToToggle) {
        try {
          const newState = !roleToToggle.active ? 1 : 0;
          await http.put(`/authorization/roles/${statusConfirmId}`, {
            state: newState,
          });
          toggleRoleActive(statusConfirmId);
          toast.success("Estado actualizado con éxito");
        } catch (error) {
          toast.error("Error", { description: "No se pudo cambiar el estado del rol." });
        }
      }
      setStatusConfirmId(null);
    }
  };

  // ─── Iniciar edicion de permiso ────────────────────────────────────────────────
  const startEditPermiso = (perm: Permission, e: React.MouseEvent) => {
    e.stopPropagation();

    setEditingPermId(perm.id);
    setIsCreatingPermission(true);

    setValuePerm('name', perm.name);
    setValuePerm('description', perm.description || '');
    setValuePerm('is_group', perm.is_group || false);
    setValuePerm('parent', perm.parent ? String(perm.parent) : null);
    setValuePerm('action', perm.action || '');
  };

  const toggleDraftPermission = (permId: string) => {
    if (!editDraft) return
    setEditDraft((d) => {
      if (!d) return d
      return d.permissions.includes(permId)
        ? { ...d, permissions: d.permissions.filter((p) => p !== permId) }
        : { ...d, permissions: [...d.permissions, permId] }
    })
  }

  const toggleCategoryAll = (category: string) => {
    if (!editDraft) return
    const catPerms = (grouped[category] ?? []).map((p) => p.id)
    const allChecked = catPerms.every((id) =>
      editDraft.permissions.includes(id),
    )
    setEditDraft((d) => {
      if (!d) return d
      if (allChecked) {
        return {
          ...d,
          permissions: d.permissions.filter((p) => !catPerms.includes(p)),
        }
      }
      const merged = Array.from(new Set([...d.permissions, ...catPerms]))
      return { ...d, permissions: merged }
    })
  }

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      next.has(cat) ? next.delete(cat) : next.add(cat)
      return next
    })
  }

  const activeRole = roles.find((r) => r.id === activeRoleId)
  //const displayRole = editDraft ?? activeRole;

  // ── Table helpers ───────────────────────────────────────────────────────────

  const getRoleBadge = (roleId: string) => {
    const role = roles.find((r) => r.id === roleId)
    return role
      ? { color: role.color, label: role.name }
      : { color: 'bg-slate-100 text-slate-700', label: roleId }
  }

  // ── Llamadas a la API al iniciar ──────────────────────────────────────────
  useEffect(() => {
    const fetchDatosBackend = async () => {
      setIsLoading(true);
      try {
        const [resPermisos, resRoles, resSucursales] = await Promise.all([
          http.get('/authorization/permissions'),
          http.get('/authorization/roles'),
          sucursalService.getSucursales()
        ]);

        const responsePermisos = resPermisos.data?.data || resPermisos.data;
        const responseRoles = resRoles.data?.data || resRoles.data;

        const permisosPuros = responsePermisos.permissions?.data || responsePermisos.permissions || responsePermisos;
        const rolesPuros = responseRoles.roles?.data || responseRoles.roles || responseRoles;

        let permisosPlanos: any[] = [];

        permisosPuros.forEach((p: any) => {
          permisosPlanos.push(p);

          if (p.children && p.children.length > 0) {
            permisosPlanos.push(...p.children);
          } else if (p.permissions && p.permissions.length > 0) {
            permisosPlanos.push(...p.permissions);
          }
        });


        const permisosMapeados: Permission[] = permisosPlanos.map((p: any) => ({
          id: String(p.id),
          name: p.name,
          description: p.description || '',
          is_group: p.parent === null,
          parent: p.parent ? String(p.parent) : null,
          action: p.action || null,
          code: Number(p.code),
        }));

        const rolesMapeados: Role[] = rolesPuros.map((r: any, index: number) => {
          const theme = ROLE_COLORS[index % ROLE_COLORS.length];
          return {
            id: String(r.id),
            name: r.name,
            description: r.description || 'Sin descripción',
            color: theme.badge,
            badgeColor: theme.dot,
            isSystem: r.id <= 4,
            active: r.state === 1,
            users_count: Number(r.users_count || 0),
            permissions: Array.isArray(r.permissions)
              ? r.permissions.map((p: any) => String(p.id || p))
              : []
          };
        });


        const responseSucursales = resSucursales.data || resSucursales;
        const sucursalesPuros = Array.isArray(responseSucursales) ? responseSucursales : (responseSucursales.data || responseSucursales.sucursales || []);

        const sucursalesMapeadas = [
          { id: 'todas', name: 'Todas las sucursales', address: 'Acceso global' },
          ...sucursalesPuros.map((s: any) => ({
            id: String(s.id),
            name: s.name,
            address: s.address || 'Sin dirección'
          }))
        ];

        setDbPermissions(permisosMapeados);
        setRoles(rolesMapeados);
        setSucursales(sucursalesMapeadas);

      } catch (error) {
        console.error("No se pudieron cargar los datos iniciales:", error);
        toast.error("Error de conexión", {
          description: "No se pudieron cargar los roles y permisos del sistema."
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDatosBackend();
  }, [refreshCount]);

  const fetchUsers = async (page = 1, search = searchQuery) => {
    setIsLoading(true);
    try {
      const res = await userService.getUsers({
        page,
        per_page: pagination.perPage,
        search,
      });

      const responseUsers = res.data?.data || res.data || res;
      const usersPuros = Array.isArray(responseUsers) ? responseUsers : (responseUsers.data || responseUsers.users || []);

      const usersMapeados: User[] = usersPuros.map((u: any) => ({
        id: u.id,
        name: u.name,
        last_name: u.last_name || '',
        email: u.email,
        phone_number: u.phone_number || '',
        avatar_url: u.avatar_url,
        role: u.role?.name || String(u.role_id),
        role_id: u.role_id,
        branch: u.access_all_sucursals ? 'Todas' : (u.sucursals?.map((s: any) => s.name).join(', ') || 'Sin sucursal'),
        lastAccess: u.last_access || 'Nunca',
        active: u.is_active === 1 || u.is_active === true,
        use_pin_login: u.use_pin_login === 1 || u.use_pin_login === true,
        access_all_sucursals: u.access_all_sucursals === 1 || u.access_all_sucursals === true,
        sucursal_ids: u.sucursal_ids || u.sucursals?.map((s: any) => s.id) || [],
        sucursals: u.sucursals || []
      }));

      const paginator = res.data || {};
      
      setUsers(usersMapeados);
      setPagination({
          currentPage: paginator.current_page || 1,
          total: paginator.total || usersMapeados.length,
          perPage: paginator.per_page || 10,
          lastPage: paginator.last_page || 1,
          from: paginator.from || 0,
          to: paginator.to || 0,
      });
    } catch (error) {
      toast.error('Error al cargar los usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(pagination.currentPage, searchQuery);
  }, [pagination.currentPage, searchQuery, refreshCount]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };
  // ── Manejo de pasos ───────────────────────────────────────────────────────────
  const NextStep = async () => {
    const isStep1Valid = await trigger([
      'nombre', 'apellidos', 'email', 'telefono', 'password', 'confirmarPassword', 'pin', 'confirmarPin'
    ]);

    const currentValues = getValues();
    let hasManualErrors = false;

    if (!editingUserId) {
      if (!currentValues.password) {
        setError('password', { type: 'manual', message: 'La contraseña es requerida' });
        hasManualErrors = true;
      }
      if (currentValues.use_pin_login && !currentValues.pin) {
        setError('pin', { type: 'manual', message: 'El PIN es requerido' });
        hasManualErrors = true;
      }
    }

    if (isStep1Valid && !hasManualErrors) {
      setFormStep(2);
    } else {
      toast.error("Faltan datos", {
        description: "Completa o corrige todos los campos marcados",
      });
    }
  };

  const onSubmitNewUser = async (data: UserFormValidate) => {
    try {
      const payload: any = {
        name: data.nombre,
        last_name: data.apellidos,
        email: data.email,
        phone_number: data.telefono,
        use_pin_login: data.use_pin_login,
        role_id: Number(data.rol),
        is_active: data.activo,
        access_all_sucursals: data.sucursales?.includes('todas') || false,
        sucursal_ids: data.sucursales?.includes('todas') ? [] : (data.sucursales?.map(Number) || []),
      };

      if (data.password) {
        payload.password = data.password;
      }
      if (data.use_pin_login && data.pin) {
        payload.pin = data.pin;
      }

      if (avatarFile) {
        payload.avatar = avatarFile;
      }

      if (editingUserId) {
        await userService.updateUser(editingUserId, payload);
        toast.success("Usuario actualizado correctamente");

        // Refrescar el estado global de la sesión si editamos nuestro propio usuario
        if (sessionStore.user && sessionStore.user.id === editingUserId) {
            try {
                const updatedUser = await authService.me();
                sessionStore.setUser(updatedUser);
            } catch (e) {
                console.error("Error refrescando el usuario logueado", e);
            }
        }
      } else {
        await userService.createUser(payload);
        toast.success("Usuario creado correctamente");
      }

      setShowNewUserModal(false);
      setFormStep(1);
      reset();
      setEditingUserId(null);
      setRefreshCount((prev) => prev + 1);
    } catch (error) {
      toast.error("Error al guardar usuario", {
        description: "Revisa los datos e intenta de nuevo.",
      });
      console.error(error);
    }
  };

  const executeDeleteUser = async () => {
    if (deleteUserConfirmId) {
      try {
        await userService.deleteUser(deleteUserConfirmId);
        toast.success("Usuario eliminado correctamente");
        setRefreshCount((prev) => prev + 1);
      } catch (error) {
        toast.error("Error al eliminar", {
          description: "No se pudo eliminar el usuario.",
        });
        console.error(error);
      } finally {
        setDeleteUserConfirmId(null);
      }
    }
  };

  const onErrorNewUser = (errors: any) => {
    const errorMsg = errors?.sucursales?.message || errors?.rol?.message || "Verifica que todos los campos sean correctos.";
    toast.error("Faltan datos", {
      description: errorMsg,
    });
  }

  // ─── Guardar o Actualizar rol ────────────────────────────────────────────────
  const onSubmitRol = async (data: RoleFormValidate) => {
    try {
      const codigosNumericos = data.permisos
        .map(id => dbPermissions.find(p => p.id === id)?.code)
        .filter(code => code !== undefined && code !== null);

      const payload = {
        name: data.nombre,
        description: data.descripcion,
        permissions: codigosNumericos,
        is_operations: 0,
        state: data.activo ? 1 : 0,
        color: data.color
      };

      if (isCreatingRole) {
        await http.post('/authorization/roles', payload);
        toast.success("Rol creado correctamente");
      } else if (editDraft?.id) {
        await http.put(`/authorization/roles/${editDraft.id}`, payload);
        toast.success("Rol actualizado correctamente");
      }

      resetRole();
      setEditDraft(null);
      setIsCreatingRole(false);
      setRefreshCount(prev => prev + 1);

    } catch (error) {
      toast.error("Error de servidor", {
        description: "Revisa la consola para más detalles."
      });
      console.error(error);
    }
  };

  // ── Guardar o Actualizar Permiso ──────────────────────────────────────────────
  const onSubmitPermiso = async (data: PermissionFormValidate) => {
    try {
      const payload = {
        name: data.name,
        description: data.description,
        is_group: data.is_group,
        parent: data.parent || null,
        action: data.action || null,
      };

      if (editingPermId) {
        // ACTUALIZAR
        await http.put(`/authorization/permissions/${editingPermId}`, payload);
        toast.success("Permiso actualizado con éxito");

        setIsCreatingPermission(false);
        if (roles.length > 0) setActiveRoleId(roles[0].id);
      } else {
        // CREAR NUEVO
        await http.post('/authorization/permissions', payload);
        toast.success("Permiso creado con éxito");

      }

      resetPerm();
      setEditingPermId(null);
      setRefreshCount(prev => prev + 1);

    } catch (error) {
      toast.error("Error al guardar permiso", { description: "Revisa la consola" });
    }
  };

  // Funcion para prender/apagar un permiso
  const togglePermisoZod = (idPermiso: string) => {
    // Clonamos el arreglo actual de permisos, o iniciamos uno vacio si no hay
    const actuales = permisosSeleccionados || [];
    const yaExiste = actuales.includes(idPermiso);

    // Si ya existe, lo filtramos (lo borramos), si no existe, lo agregamos al final
    const nuevosPermisos = yaExiste
      ? actuales.filter((id) => id !== idPermiso)
      : [...actuales, idPermiso];

    // Guardamos la nueva lista y validamos en tiempo real
    setValueRole('permisos', nuevosPermisos, { shouldValidate: true });
  };

  // ── Render ──────────────────────────────────────────────────────────────────



  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Usuarios y Permisos
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {users.length} usuarios activos
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar usuario..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-9 sm:w-64"
            />
          </div>
          <Button variant="outline" onClick={openRolesModal} className="w-full sm:w-auto">
            <Settings2 className="mr-2 h-4 w-4" />
            Administrar Roles
          </Button>
          <Button
            onClick={openNewUserModal}
            className="bg-[#3B7D3B] hover:bg-[#2d6a2d] w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Role summary cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {roles
          .filter((r) => r.active)
          .map((role) => {
            const count = role.users_count
            return (
              <div
                key={role.id}
                className="rounded-lg border border-slate-200 bg-white p-5"
              >
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: role.badgeColor + '1a' }}
                  >
                    <Shield
                      className="h-5 w-5"
                      style={{ color: role.badgeColor }}
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      {role.name}
                    </h3>
                    <p className="text-xl font-semibold text-slate-900">
                      {count}
                    </p>
                  </div>
                </div>
                <p className="line-clamp-2 text-xs text-slate-500">
                  {role.description}
                </p>
              </div>
            )
          })}
      </div>

      {/* Users table */}
      {isLoading ? (
        <TableSkeleton
          headers={['Usuario', 'Email', 'Rol', 'Sucursal', 'Estado', 'Acciones']}
          columns={['text', 'text', 'badge', 'text', 'badge', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Sucursal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.length > 0 ? (
                  users.map((user) => {
                    const { color, label } = getRoleBadge(String(user.role_id))
                    return (
                      <tr key={user.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {user.name} {user.last_name}
                        </td>
                        <td className="px-6 py-4 text-slate-700">{user.email}</td>
                        <td className="px-6 py-4">
                          <Badge className={color}>{label}</Badge>
                        </td>
                        <td className="px-6 py-4 text-slate-700">{user.branch}</td>
                        <td className="px-6 py-4">
                          <Badge
                            className={
                              user.active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }
                          >
                            {user.active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditUserModal(user)}
                              className="rounded p-1 hover:bg-slate-100"
                              title="Editar Usuario"
                            >
                              <Edit className="h-4 w-4 text-slate-600" />
                            </button>
                            <button
                              onClick={() => setDeleteUserConfirmId(user.id)}
                              className="rounded p-1 hover:bg-red-50"
                              title="Eliminar Usuario"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <UserIcon className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron usuarios</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Controles de Paginacion */}
          {users.length > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={(page) =>
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                }))
              }
            />
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL: Administrar Roles
      ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showRolesModal}
        onOpenChange={(open) => {
          if (!open) {
            setEditDraft(null)
            setIsCreatingRole(false)
            setDeleteConfirmId(null)
            setIsCreatingPermission(false)
            resetRole()
            resetPerm()
          }
          setShowRolesModal(open)
        }}
      >
        <DialogContent className="flex h-[80vh] min-h-[600px] flex-col overflow-hidden p-0 sm:max-w-4xl">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-5 pb-4">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Settings2 className="h-5 w-5 text-[#3B7D3B]" />
              Administrar Roles
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-sm text-slate-500">
              Configura los roles del sistema y sus permisos por módulo.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-1 overflow-hidden">
            {/* ── Left panel: role list ───────────────────────────────── */}
            <div className="flex w-64 shrink-0 flex-col border-r border-slate-200 bg-slate-50">
              <div className="space-y-2 border-b border-slate-200 p-3">
                {/* Botonon 'Nuevo Rol' */}
                <button
                  onClick={() => {
                    setIsCreatingPermission(false)
                    startCreate()
                  }}
                  className='flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-[#3B7D3B] hover:bg-green-50 hover:text-[#3B7D3B]'
                >
                  <Plus className='h-4 w-4' />
                  Nuevo Rol
                </button>
                {/* Boton 'Nuevo Permiso' */}
                <button
                  onClick={() => {
                    setIsCreatingPermission(true)
                    setEditDraft(null)
                    setActiveRoleId('')
                    setEditingPermId(null)
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-[#3B7D3B] hover:bg-green-50 hover:text-[#3B7D3B]"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo Permiso
                </button>
              </div>

              <div className="flex-1 space-y-1 overflow-y-auto p-2">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={async () => {
                      if (activeRoleId !== role.id) {
                        setIsSwitchingRole(true)
                        setActiveRoleId(role.id)
                        setEditDraft(null)
                        setIsCreatingRole(false)


                        // SE CREA UN DELAY ARTIFICIAL (VISUAL, SE PUEDE ELIMINAR)
                        await new Promise((resolve) => setTimeout(resolve, 140))

                        setIsSwitchingRole(false)
                      } else {
                        setEditDraft(null)
                        setIsCreatingRole(false)
                      }
                    }}
                    className={`group w-full rounded-lg px-3 py-2.5 text-left transition-colors ${activeRoleId === role.id && !isCreatingRole
                      ? 'border border-slate-200 bg-white shadow-sm'
                      : 'hover:bg-white'
                      }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor: role.badgeColor,
                          opacity: role.active ? 1 : 0.4,
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={`truncate text-sm font-medium ${role.active ? 'text-slate-900' : 'text-slate-400'}`}
                        >
                          {role.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {role.permissions.length} permiso
                          {role.permissions.length !== 1 ? 's' : ''}
                          {role.isSystem && ' · Sistema'}
                        </p>
                      </div>
                      {!role.active && (
                        <span className="shrink-0 text-xs text-slate-400">
                          Off
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Right panel: role editor / viewer ──────────────────── */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {isCreatingPermission ? (

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">

                  {/* Encabezado del panel */}
                  <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
                    <h3 className="font-semibold text-slate-900">Nuevo Permiso Técnico</h3>
                  </div>

                  {/* Cuerpo del formulario */}
                  <div className="flex-1 space-y-6 overflow-y-auto p-6">

                    {/* ── Nombre y Descripción ── */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-slate-700">
                          Nombre del permiso <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Ej: Cobrar en caja"
                          {...registerPerm('name')}
                          className={errorsPerm.name ? 'border-red-400' : ''}
                        />
                        <FormError message={errorsPerm.name?.message} />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-slate-700">
                          Descripción <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Ej: Permite al usuario procesar pagos"
                          {...registerPerm('description')}
                          className={errorsPerm.description ? 'border-red-400' : ''}
                        />
                        <FormError message={errorsPerm.description?.message} />
                      </div>
                    </div>

                    {/* ── Switch: ¿Es Grupo? ── */}
                    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <Checkbox
                        id="is_group_check"
                        checked={isGroupSelected}
                        onCheckedChange={(checked) => setValuePerm('is_group', checked === true, { shouldValidate: true })}
                      />
                      <div>
                        <label htmlFor="is_group_check" className="cursor-pointer font-medium text-slate-900">
                          Es un grupo (Módulo)
                        </label>
                        <p className="text-xs text-slate-500">
                          Desmarca esta opción si vas a crear una acción específica dentro de un módulo.
                        </p>
                      </div>
                    </div>

                    {/* ── Campos Condicionales (Solo si NO es grupo) ── */}
                    {!isGroupSelected && (
                      <div className="grid grid-cols-2 gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4">

                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700">
                            Grupo <span className="text-red-500">*</span>
                          </label>
                          <select
                            {...registerPerm('parent')}
                            className={`flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B7D3B] focus:border-transparent ${errorsPerm.parent ? 'border-red-400' : 'border-slate-200'
                              }`}
                          >
                            <option value="">Selecciona un módulo...</option>

                            {dbPermissions
                              .filter(p => p.parent === null)
                              .map((modulo) => (
                                <option key={modulo.id} value={modulo.id}>
                                  {modulo.name}
                                </option>
                              ))}
                          </select>
                          <FormError message={errorsPerm.parent?.message} />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700">
                            Código de Acción <span className="text-red-500">*</span>
                          </label>
                          <Input
                            placeholder="Ej: Customer.create"
                            {...registerPerm('action')}
                            className={errorsPerm.action ? 'border-red-400' : ''}
                          />
                          <FormError message={errorsPerm.action?.message} />
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Barra de guardado */}
                  <div className="flex shrink-0 gap-3 border-t border-slate-200 bg-white px-6 py-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setIsCreatingPermission(false)
                        setEditingPermId(null)
                        resetPerm()
                      }}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSubmitPerm(onSubmitPermiso)}
                      disabled={isSubmittingPerm}
                      className="flex-1 bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                    >
                      {isSubmittingPerm ? (
                        <Spinner className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="mr-2 h-4 w-4" />
                      )}
                      {isSubmittingPerm ? 'Creando...' : 'Crear Permiso'}
                    </Button>
                  </div>
                </div>
              ) : editDraft ? (
                /* Edit / Create form */
                /*Formulario para crear nuevos roles*/
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
                    <h3 className="font-semibold text-slate-900">
                      {isCreatingRole
                        ? 'Nuevo Rol'
                        : `Editando: ${editDraft.name || 'Sin nombre'}`}
                    </h3>
                    <button
                      onClick={cancelEdit}
                      className="rounded p-1 hover:bg-slate-100"
                    >
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  <div className="flex-1 space-y-6 overflow-y-auto p-6">
                    {/* Basic info - Rol */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">

                        {/* input para el nombre del rol */}
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700">
                            Nombre del rol <span className="text-red-500">*</span>
                          </label>
                          <Input
                            placeholder='Escribe el nombre del Rol'
                            {...registerRole('nombre')}
                            className={errorsRole.nombre ? 'border-red-500' : ''}
                            disabled={editDraft.isSystem}
                          />
                          <FormError message={errorsRole.nombre?.message} />

                          {editDraft.isSystem && (
                            <p className="flex items-center gap-1 text-xs text-slate-400">
                              <AlertCircle className="h-3 w-3" /> Nombre protegido (rol del sistema)
                            </p>
                          )}
                        </div>

                        {/* input para el color de insignia del rol */}
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-slate-700">
                            Color de insignia
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {ROLE_COLORS.map((c) => (
                              <button
                                key={c.dot}
                                type='button'
                                title={c.label}
                                onClick={() =>
                                  setValueRole('color', c.badge, { shouldValidate: true })
                                }
                                className={`h-6 w-6 rounded-full border-2 transition-all ${colorSeleccionado === c.badge
                                  ? 'scale-110 border-slate-900'
                                  : 'border-transparent hover:scale-105'
                                  }`}
                                style={{ backgroundColor: c.dot }}
                              />
                            ))}
                          </div>
                          <FormError message={errorsRole.color?.message} />
                        </div>
                      </div>
                      {/* el input del descripcion */}
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-slate-700">
                          Descripción
                        </label>
                        <Input
                          placeholder="Describe brevemente las responsabilidades de este rol"
                          {...registerRole('descripcion')}
                          className={errorsRole.descripcion ? 'border-red-500' : ''}
                        />
                        <FormError message={errorsRole.descripcion?.message} />
                      </div>
                    </div>

                    {/* Asignacion de permisos */}
                    <div>
                      {/* ── HEADER DE PERMISOS Y ERROR DE ZOD ── */}
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-slate-900">
                          Funciones y permisos
                        </h4>
                        <span className="text-xs text-slate-500">
                          {permisosSeleccionados?.length || 0} de {dbPermissions.length} seleccionados
                        </span>
                      </div>

                      <FormError message={errorsRole.permisos?.message} />

                      {/* ── CICLO DE CATEGORÍAS ── */}
                      <div className="space-y-2 mt-2">
                        {Object.entries(grouped).map(([category, perms]) => {
                          const catIds = perms.map((p) => p.id);
                          const checkedCount = catIds.filter((id) =>
                            (permisosSeleccionados || []).includes(id)
                          ).length;
                          const allChecked = checkedCount === catIds.length;
                          const someChecked = checkedCount > 0 && !allChecked;
                          const isCollapsed = collapsedCategories.has(category);

                          return (
                            <div key={category} className="overflow-hidden rounded-lg border border-slate-200">

                              {/* ACORDEÓN DE LA CATEGORÍA (Padre) */}
                              <div className="group flex items-center gap-3 bg-slate-50 px-4 py-2.5">
                                <label className="flex-1 cursor-pointer text-sm font-medium text-slate-800">
                                  {category}
                                </label>

                                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      const padreObj = dbPermissions.find(p => p.name === category && p.parent === null);
                                      if (padreObj) startEditPermiso(padreObj, e);
                                    }}
                                    className="rounded p-1 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                                    title="Editar Módulo"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const padreObj = dbPermissions.find(p => p.name === category && p.parent === null);
                                      if (padreObj) setDeletePermConfirmId(padreObj.id);
                                    }}
                                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                    title="Eliminar Módulo"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>

                                <span className="mr-1 text-xs text-slate-500">
                                  {checkedCount}/{catIds.length}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => toggleCategory(category)}
                                  className="rounded p-0.5 text-slate-500 hover:bg-slate-200"
                                >
                                  {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                </button>
                              </div>

                              {/* LISTA DE CHECKBOXES DENTRO DE LA CATEGORÍA */}
                              {!isCollapsed && (
                                <div className="divide-y divide-slate-100">
                                  {perms.map((perm) => {
                                    // Comprobamos si Zod tiene guardado este ID
                                    const checked = (permisosSeleccionados || []).includes(perm.id);

                                    return (
                                      <div
                                        key={perm.id}
                                        // 🚨 Le agregamos 'group' aquí y separamos la lógica de clic
                                        className={`group flex cursor-pointer items-center justify-between px-4 py-2.5 transition-colors ${checked ? 'bg-green-50' : 'hover:bg-slate-50'
                                          }`}
                                      >
                                        {/* Contenedor del checkbox */}
                                        <div className="flex flex-1 items-center gap-3" onClick={() => togglePermisoZod(perm.id)}>
                                          <Checkbox
                                            id={`perm-${perm.id}`}
                                            checked={checked}
                                            onCheckedChange={() => togglePermisoZod(perm.id)}
                                            className="pointer-events-none"
                                          />
                                          <label
                                            htmlFor={`perm-${perm.id}`}
                                            className={`cursor-pointer text-sm ${checked ? 'font-medium text-slate-900' : 'text-slate-600'
                                              }`}
                                          >
                                            {perm.name}
                                          </label>
                                          {checked && <Check className="h-3.5 w-3.5 shrink-0 text-[#3B7D3B]" />}
                                        </div>

                                        {/* 🚨 NUEVO: Botones de acción ocultos que aparecen al pasar el mouse */}
                                        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                          <button
                                            type="button"
                                            onClick={(e) => startEditPermiso(perm, e)}
                                            className="rounded p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                                            title="Editar Permiso"
                                          >
                                            <Edit className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) => setDeletePermConfirmId(perm.id)}
                                            className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                            title="Eliminar Permiso"
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Save bar */}
                  <div className="flex shrink-0 gap-3 border-t border-slate-200 bg-white px-6 py-4">
                    <Button
                      variant="outline"
                      onClick={cancelEdit}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSubmitRole(onSubmitRol)}
                      disabled={isSubmittingRole}
                      className="flex-1 bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                    >
                      {isSubmittingRole ? (
                        <Spinner className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="mr-2 h-4 w-4" />
                      )}
                      {isSubmittingRole ? 'Guardando...' : isCreatingRole ? 'Crear Rol' : 'Guardar Cambios'}
                    </Button>

                  </div>
                </div>
              ) : isSwitchingRole ? (
                <DiscreteLoader text="Cargando permisos..." />
              ) : activeRole ? (
                /* Read-only view of selected role */
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: activeRole.badgeColor }}
                      />
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {activeRole.name}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {activeRole.permissions.length} permisos asignados
                        </p>
                      </div>
                      <Badge className={activeRole.color}>
                        {activeRole.isSystem ? 'Sistema' : 'Personalizado'}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setStatusConfirmId(activeRole.id)}
                        title={activeRole.active ? 'Desactivar rol' : 'Activar rol'}
                        className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                      >
                        {activeRole.active ? (
                          <ToggleRight className="h-5 w-5 text-[#3B7D3B]" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>

                      {!activeRole.isSystem && (
                        <button
                          onClick={() => setDeleteConfirmId(activeRole.id)}
                          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          title="Eliminar Rol"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}

                      <Button
                        onClick={() => startEdit(activeRole)}
                        size="sm"
                        variant="outline"
                      >
                        <Edit className="mr-1.5 h-3.5 w-3.5" />
                        Editar
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 space-y-5 overflow-y-auto p-6">
                    <p className="text-sm text-slate-600">
                      {activeRole.description}
                    </p>

                    {/* Permissions by category (read-only) */}
                    <div className="space-y-2">
                      {Object.entries(grouped).map(([category, perms]) => {
                        const active = perms.filter((p) =>
                          activeRole.permissions.includes(p.id),
                        )
                        if (active.length === 0) return null
                        return (
                          <div
                            key={category}
                            className="overflow-hidden rounded-lg border border-slate-200"
                          >
                            <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5">
                              <span className="text-sm font-medium text-slate-800">
                                {category}
                              </span>
                              <span className="text-xs text-slate-500">
                                {active.length}/{perms.length}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 p-3">
                              {perms.map((perm) => {
                                const has = activeRole.permissions.includes(
                                  perm.id,
                                )
                                return (
                                  <span
                                    key={perm.id}
                                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${has
                                      ? 'border-green-200 bg-green-50 text-green-800'
                                      : 'border-slate-200 bg-slate-50 text-slate-400 line-through'
                                      }`}
                                  >
                                    {perm.name}
                                  </span>
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                      {/* Categories with no permissions */}
                      {Object.entries(grouped).every(([, perms]) =>
                        perms.every(
                          (p) => !activeRole.permissions.includes(p.id),
                        ),
                      ) && (
                          <p className="py-6 text-center text-sm text-slate-400">
                            Sin permisos asignados
                          </p>
                        )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
                  Selecciona un rol para ver sus detalles
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          MODAL: Nuevo Usuario (2 steps)
      ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showNewUserModal}
        onOpenChange={(open) => {
          setShowNewUserModal(open)
          if (!open) {
            setFormStep(1)
            reset();
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-900">
              {editingUserId ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Registrar nuevo usuario en el sistema
            </DialogDescription>
          </DialogHeader>

          <div className="mb-6 flex items-center gap-2">
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${formStep === 1 ? 'bg-[#3B7D3B] text-white' : 'bg-green-100 text-green-700'}`}
            >
              {formStep > 1 ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <span>1</span>
              )}
              Datos personales
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${formStep === 2 ? 'bg-[#3B7D3B] text-white' : 'bg-slate-100 text-slate-500'}`}
            >
              <span>2</span>
              Rol y sucursales
            </div>
          </div>

          {formStep === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-4 border-b border-slate-100 pb-4">
                <div className="flex h-16 w-16 overflow-hidden items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  {avatarFile ? (
                    <img src={URL.createObjectURL(avatarFile)} alt="Avatar" className="h-full w-full object-cover" />
                  ) : editingUserId && users.find(u => u.id === editingUserId)?.avatar_url ? (
                    <img src={users.find(u => u.id === editingUserId)?.avatar_url!} alt="Avatar" className="h-full w-full object-cover" />
                  ) : (
                    <UserIcon className="h-8 w-8" />
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-900">Avatar del usuario</h4>
                  <p className="text-xs text-slate-500">Selecciona una imagen para el perfil del usuario (opcional).</p>
                  <label className="mt-2 inline-block cursor-pointer">
                    <span className="inline-flex h-8 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2">
                      Subir foto
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setAvatarFile(file);
                      }}
                    />
                  </label>
                  {avatarFile && (
                    <button
                      type="button"
                      onClick={() => setAvatarFile(null)}
                      className="ml-2 mt-2 text-xs text-red-500 hover:text-red-700"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <UserIcon className="h-3.5 w-3.5" />
                    Nombre(s) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: Brandon"
                    {...register("nombre")}
                    className={errors.nombre ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.nombre?.message} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Apellidos <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: Navarrete Flores"
                    {...register("apellidos")}
                    className={errors.apellidos ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.apellidos?.message} />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Mail className="h-3.5 w-3.5" />
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="usuario@farmaciassaludplus.com"
                  {...register("email")}
                  className={errors.email ? 'border-red-400' : ''}
                />
                <FormError message={errors.email?.message} />
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Phone className="h-3.5 w-3.5" />
                  Teléfono
                </label>
                <Input
                  type="tel"
                  placeholder="Ej: 667 123 4567"
                  {...register("telefono")}
                  className={errors.telefono ? 'border-red-400' : ''}
                />
                <FormError message={errors.telefono?.message} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <Lock className="h-3.5 w-3.5" />
                    Contraseña {!editingUserId && <span className="text-red-500">*</span>}
                  </label>
                  <Input
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    {...register("password")}
                    className={errors.password ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.password?.message} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Confirmar contraseña {!editingUserId && <span className="text-red-500">*</span>}
                  </label>
                  <Input
                    type="password"
                    placeholder="Repetir contraseña"
                    {...register("confirmarPassword")}
                    className={errors.confirmarPassword ? 'border-red-400' : ''}
                  />
                  <FormError message={errors.confirmarPassword?.message} />
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <Checkbox
                  id="use-pin"
                  checked={watch('use_pin_login')}
                  onCheckedChange={(checked) =>
                    setValue('use_pin_login', !!checked, { shouldValidate: true })}
                />
                <div>
                  <label
                    htmlFor="use-pin"
                    className="cursor-pointer text-sm font-medium text-slate-700"
                  >
                    Usar PIN para inicio de sesión
                  </label>
                  <p className="text-xs text-slate-500">
                    Permite al usuario iniciar sesión rápidamente con un PIN de 4 dígitos
                  </p>
                </div>
              </div>

              {watch('use_pin_login') && (
                <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">
                      PIN (4 dígitos) {(!editingUserId || watch('use_pin_login')) && <span className="text-red-500">*</span>}
                    </label>
                    <Input
                      type="password"
                      maxLength={4}
                      placeholder="Ej: 1234"
                      {...register("pin")}
                      className={errors.pin ? 'border-red-400' : ''}
                      onChange={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, '');
                        register("pin").onChange(e);
                      }}
                    />
                    <FormError message={errors.pin?.message} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">
                      Confirmar PIN {(!editingUserId || watch('use_pin_login')) && <span className="text-red-500">*</span>}
                    </label>
                    <Input
                      type="password"
                      maxLength={4}
                      placeholder="Repetir PIN"
                      {...register("confirmarPin")}
                      className={errors.confirmarPin ? 'border-red-400' : ''}
                      onChange={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, '');
                        register("confirmarPin").onChange(e);
                      }}
                    />
                    <FormError message={errors.confirmarPin?.message} />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <Checkbox
                  id="user-active"
                  checked={activoValue}
                  onCheckedChange={(checked) =>
                    setValue('activo', !!checked, { shouldValidate: true })}
                />
                <div>
                  <label
                    htmlFor="user-active"
                    className="cursor-pointer text-sm font-medium text-slate-700"
                  >
                    Usuario activo
                  </label>
                  <p className="text-xs text-slate-500">
                    El usuario podrá iniciar sesión inmediatamente
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type='button'
                  onClick={NextStep}
                  className="bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                >
                  Siguiente <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ─── PASO 2 ────────────────────────────────────────────── */}
          {formStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="mb-3 block text-sm font-medium text-slate-700">
                  Rol del usuario <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {roles
                    .filter((r) => r.active)
                    .map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => setValue('rol', role.id, { shouldValidate: true })}

                        className={`rounded-lg border-2 p-4 text-left transition-all ${rolValue === role.id
                          ? 'border-[#3B7D3B] bg-green-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: role.badgeColor }}
                            />
                            <span
                              className={`text-sm font-semibold ${rolValue === role.id ? 'text-[#3B7D3B]' : 'text-slate-900'
                                }`}
                            >
                              {role.name}
                            </span>
                          </div>
                          {rolValue === role.id && (
                            <div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#3B7D3B]">
                              <Check className="h-2.5 w-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed text-slate-500">
                          {role.description}
                        </p>
                      </button>
                    ))}
                </div>
                {errors.rol && (
                  <p className="mt-2 text-xs text-red-500">{errors.rol.message}</p>
                )}

                {rolValue && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="mb-1.5 text-xs font-medium text-slate-600">
                      Permisos de {roles.find((r) => r.id === rolValue)?.name}:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(roles.find((r) => r.id === rolValue)?.permissions ?? []).map((permId) => {
                        const perm = dbPermissions.find((p) => p.id === permId);
                        return perm ? (
                          <span
                            key={permId}
                            className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-600"
                          >
                            {perm.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Building2 className="h-3.5 w-3.5" />
                  Acceso a sucursales <span className="text-red-500">*</span>
                </label>
                <p className="mb-3 text-xs text-slate-500">
                  Selecciona las sucursales a las que tendrá acceso este usuario
                </p>

                <FormError message={errors.sucursales?.message} />
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  {sucursales.map((suc, idx) => {
                    const currentSucursales = sucursalesValue || [];
                    const isChecked = currentSucursales.includes(String(suc.id));
                    const isDisabledByAll = suc.id !== 'todas' && currentSucursales.includes('todas');

                    return (
                      <div
                        key={suc.id}
                        onClick={() => !isDisabledByAll && toggleSucursal(String(suc.id))}
                        className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${idx !== 0 ? 'border-t border-slate-100' : ''
                          } ${isChecked || isDisabledByAll ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                      >
                        <Checkbox
                          id={`suc-${suc.id}`}
                          checked={isChecked || isDisabledByAll}
                          onCheckedChange={() => !isDisabledByAll && toggleSucursal(String(suc.id))}
                          className="pointer-events-none"
                        />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${isChecked || isDisabledByAll ? 'text-[#3B7D3B]' : 'text-slate-700'}`}>
                            {suc.name}
                          </p>
                          <p className="truncate text-xs text-slate-500">{suc.address}</p>
                        </div>
                        {(isChecked || isDisabledByAll) && (
                          <Check className="h-4 w-4 shrink-0 text-[#3B7D3B]" />
                        )}
                      </div>
                    );
                  })}
                </div>
                {(sucursalesValue || []).length > 0 && !(sucursalesValue || []).includes('todas') && (
                  <p className="mt-2 text-xs text-slate-500">
                    {(sucursalesValue || []).length} sucursal
                    {(sucursalesValue || []).length !== 1 ? 'es' : ''} seleccionada
                    {(sucursalesValue || []).length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormStep(1)}
                  className="flex-1"
                >
                  Atrás
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit(onSubmitNewUser, onErrorNewUser)}
                  className="flex-1 bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4 text-white" />
                      {editingUserId ? 'Guardando...' : 'Creando Usuario...'}
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      {editingUserId ? 'Guardar' : 'Crear Usuario'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* ═══════════════════════════════════════════════════════════
          MODALS DE CONFIRMACIÓN
      ══════════════════════════════════════════════════════════════ */}
      <ConfirmationModal
        isOpen={deletePermConfirmId !== null}
        onClose={() => setDeletePermConfirmId(null)}
        onConfirm={executeDeletePermiso}
        preset="delete"
        itemName="permiso"
      />

      <ConfirmationModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={executeDeleteRole}
        preset="delete"
        itemName="rol"
      />

      <ConfirmationModal
        isOpen={statusConfirmId !== null}
        onClose={() => setStatusConfirmId(null)}
        onConfirm={executeChangeStatus}
        preset="toggleStatus"
        itemName="rol"
      />

      <ConfirmationModal
        isOpen={deleteUserConfirmId !== null}
        onClose={() => setDeleteUserConfirmId(null)}
        onConfirm={executeDeleteUser}
        preset="delete"
        itemName="usuario"
      />
    </div>
  )
}
